<template>
  <b-modal
    id="show-image-modal"
    size="xl"
    @show="onShow"
  >
    <template v-slot:modal-title>
      {{ trans('image') }}
      <small class="text-muted">
        #{{ image.id }}
      </small>
      <sup v-if="image.is_from_canva">
        <b-badge variant="canva">
          Canva
        </b-badge>
      </sup>
      <sup v-if="image.publish_to_partner">
        <b-badge variant="primary">
          {{ organization.can_publish && organization.partner == image.publish_to_partner ? trans('published') : image.publish_to_partner }}
        </b-badge>
      </sup>
    </template>
    <b-row>
      <b-col v-if="image" class="text-center">
        <b-aspect aspect="16:9">
          <img :src="image.url" :alt="image.name" class="img-fluid" />
        </b-aspect>
        <p>
          <b-link :href="image.url" target="_blank">
            <i class="fa fa-fw fa-external-link-alt"></i>
            {{ trans('download') }}
          </b-link>
        </p>
        <b-row v-if="image.is_from_canva">
          <b-col class="text-left">
            <p>
              <small>
                {{ trans('image_from_canva') }}
              </small>
              <a :href="`/organization/${organization.id}/canva`">
                <small>
                  <i class="fa fa-fw fa-cog"></i>
                  {{ trans('canva_settings') }}
                </small>
              </a>
            </p>
            <p v-if="!!canvaApi && image.canva_id">
              <b-button variant="canva" size="sm" @click="onClickModifyInCanva">
                <i class="fa fa-fw fa-edit"></i>
                {{ trans('edit_copy_in_canva') }}
              </b-button>
            </p>
          </b-col>
          <b-col v-if="showCrop" class="col-auto">
            <b-button variant="outline-primary" size="sm" class="py-0" @click="onClickCrop">
              {{ trans('crop_resize') }}
            </b-button>
          </b-col>
        </b-row>
      </b-col>

      <b-col v-if="image" class="mt-3">
        <b-form-group :label="trans('name')">
          <b-form-input v-model="name" :disabled="busy || disabled" />
        </b-form-group>
        <b-form-group :label="trans('description')">
          <b-form-textarea v-model="description" :disabled="busy || disabled" />
        </b-form-group>
        <b-form-group v-if="organization.can_publish">
          <b-form-checkbox
            v-model="publish_to_partner"
            :value="organization.partner"
            :unchecked-value="null"
            :disabled="busy || disabled"
          >
            {{ trans('published_to_partner').replace(':partner', organization.partner) }}
          </b-form-checkbox>
        </b-form-group>
        <a :href="`/organization/${this.organization.id}/sendout/create?media_id=${image.id}`" class="btn btn-primary">
          <i class="fa fa-fw fa-comment"></i>
          {{ trans('send') }}
        </a>
      </b-col>
    </b-row>

    <p v-if="busy">
      <i class="fa fa-fw fa-circle-notch fa-spin"></i>
      {{ trans('please_wait') }}
    </p>
    <p v-if="error" class="text-danger">
      {{ error }}
    </p>

    <template v-slot:modal-footer>
      <b-row class="w-100">
        <b-col v-if="!publish_to_partner || organization.can_publish" class="col-auto">
          <delete-button
            variant="outline-danger"
            :type="!image.deleted_at ? 'archive' : 'delete'"
            :message="trans(!image.deleted_at ? 'archive_confirm' : 'media_delete_confirm')"
            @confirm="onDelete"
            :disabled="busy"
          >
            <i :class="`fa fa-fw fa-${!image.deleted_at ? 'trash' : 'times'}`"></i>
            {{ trans(!image.deleted_at ? 'archive' : 'delete') }}
          </delete-button>
        </b-col>
        <b-col class="text-right">
          <b-button
            variant="light"
            @click="$bvModal.hide('show-image-modal')"
          >
            {{ trans(!disabled ? 'cancel' : 'close') }}
          </b-button>
          <b-button
            v-if="!publish_to_partner || organization.can_publish"
            variant="primary"
            @click="onSave"
            :disabled="busy"
          >
            {{ trans('save') }}
          </b-button>
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>

<script>
  import DeleteButton from './../../../common/components/DeleteConfirmButton'

  export default {
    props: {
      organization: Object,
      image: Object,
      showCrop: {
        type: Boolean,
        default: false,
      },
    },
    components: {
      DeleteButton,
    },
    data() {
      return {
        trans: trans,
        busy: false,
        error: null,
        name: null,
        description: null,
        publish_to_partner: null,
      }
    },
    computed: {
      disabled() {
        return this.publish_to_partner && !this.organization.can_publish
      },
      canvaApi() {
        return window.canvaApi
      },
    },
    methods: {
      onClickCrop() {
        this.$emit('crop', this.image)
      },
      onClickModifyInCanva() {
        this.$emit('canva')
        this.$bvModal.hide('show-image-modal')
      },
      onSave() {
        this.busy = true
        this.error = null
        let url = `/organization/${this.organization.id}/media/image/${this.image.id}`
        let data = {
          name: this.name,
          description: this.description,
        }
        if (this.organization.can_publish) data.publish_to_partner = this.publish_to_partner
        axios.patch(url, data)
        .then(response => {
          this.busy = false
          this.$emit('saved')
          this.$bvModal.hide('show-image-modal')
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onDelete() {
        this.busy = true
        this.error = null
        let url = `/organization/${this.organization.id}/media/image/${this.image.id}`
        axios.delete(url)
        .then(response => {
          this.busy = false
          this.$emit('deleted')
          this.$bvModal.hide('show-image-modal')
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      onShow() {
        if (this.image) {
          this.name = this.image.name
          this.description = this.image.description
          this.publish_to_partner = this.image.publish_to_partner
        } else {
          this.name = null
          this.description = null
          this.publish_to_partner = null
        }
      },
    },
    mounted() {
      window.imageModal = this
    },
  }
</script>